import { useState, useContext, useEffect, useMemo } from 'react'

import styled from 'styled-components'

import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material'

import { PatternFormat } from 'react-number-format'
import FormField from 'components/FormField'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined'

import tspImage from '../../../../../assets/images/tspdetails.png'

import { IntakeContext } from 'context/IntakeContext'

import theme from 'assets/theme'

import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import OnboardingMultiSelectTile from 'components/OnboardingMultiSelectTile'
import { backgroundColor } from '@mui/system'
import { useFormDebounce } from 'hooks/useFormDebounce'

const StyledTileDiv = styled.div`
  text-align: center;
  position: relative;
  padding: 16px;
  height: 100%;
  cursor: pointer;
  border: 2px solid #f1f1f1;
  background: ${(props) => (props.active ? '#0671ad' : 'transparent')};
  color: ${(props) =>
    props.active ? '#fff !important' : '#344767 !important'};
  border-radius: 0.75rem;
  transition: 0.2s transform ease-in-out;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  &.no-click {
    pointer-events: none;
  }
  &:hover {
    transition: 0.2s all ease-in-out;
    border: 2px solid #a3a7aa;
    transform: scale(1.02);
    background: ${(props) => (props.active ? '#0671ad' : '#f9f9f9')};
    color: ${(props) => (props.active ? '#fff' : '#344767')};
    > h6,
    h5 {
      color: ${(props) => (props.active ? '#fff' : '#344767')};
    }
    > span {
      background-color: ${(props) => (props.active ? '#044F79' : '#f1f1f1')};
      color: ${(props) => (props.active ? '#fff' : '#5d6d8e')};
    }
  }

  > h6,
  h5 {
    color: ${(props) => (props.active ? '#fff' : '#344767')};
    letter-spacing: 0.3px;
    font-weight: 500;
    line-height: 1.4;
    margin-top: 8px;
    &:only-child {
      margin-top: 0px;
    }
  }

  > span {
    background-color: ${(props) => (props.active ? '#044F79' : '#f9f9f9')};
    border-radius: 50%;
    color: ${(props) => (props.active ? '#fff' : '#5d729e')};
    height: 1.5em;
    width: 1.5em;
    padding: 8px;
    &.yesIcon {
      color: #4caf50;
      background-color: #dbefdc;
    }
    &.noIcon {
      color: #fd5c70;
      background-color: #fedee2;
    }
  }
  .alert-style {
    color: #fd5c70;
  }
`

export const TSPDetails = ({ handleClose, open, initialAllocation }) => {
  const [step, setStep] = useState(0)
  const [canContinue, setCanContinue] = useState(true)

  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()

  const [tspAllocation, setTspAllocation] = useState(initialAllocation)
  const [tspTotalMixPercent, setTSPTotalMixPercent] = useState(0)

  useEffect(() => {
    console.log(tspAllocation)

    const parsedData = Object.fromEntries(
      Object.entries(tspAllocation).map(([key, data]) => [
        key,
        data.percentage || 0,
      ])
    )
    updateIntakeStateAndSubmit(() =>
      updateIntakeStateAtKey({
        tspAllocation: JSON.stringify(parsedData),
      })
    )()

    const totalPercentage = Object.values(tspAllocation).reduce(
      (sum, data) => sum + (parseFloat(data.percentage) || 0),
      0
    )
    if (totalPercentage > 0 && step == 0) {
      setStep(1)
    }

    setTSPTotalMixPercent(totalPercentage)
  }, [tspAllocation, step])

  const steps = [
    // INTRO
    {
      title: 'Enter Your TSP Fund Data',
      desc: (
        <>
          We recommend heading over to{' '}
          <a
            style={{
              color: '#3498DB',
              textDecoration: 'underline',
            }}
            target="_blank"
            href="https://www.TSP.gov"
            rel="noreferrer"
          >
            www.TSP.gov
          </a>{' '}
          to sign in, then navigating to the{' '}
          <span style={{ fontWeight: 600, color: '#495057' }}>Investments</span>{' '}
          page to start copying over your fund information.
        </>
      ),
      action: (
        <>
          <img
            style={{
              alignSelf: 'center',
              objectFit: 'contain',
              width: '100%',
              height: '100%',
              marginTop: '24px',
              border: '1px solid #D2D6DA',
              borderRadius: 12,
            }}
            src={tspImage}
          ></img>
        </>
      ),
    },
    // WHICH FUNDS ARE YOU IN?
    {
      title: 'Which Funds Are You Invested In?',
      desc: (
        <>
          Select all that apply:
          <br />
          <a
            style={{
              color: '#3498DB',
              textDecoration: 'underline',
            }}
            target="_blank"
            href="https://www.TSP.gov"
            rel="noreferrer"
          >
            Can&apos;t remember which funds invested in?
          </a>
        </>
      ),
      action: (
        <>
          <Box mt={3}>
            <Grid container spacing={1} columns={6}>
              {Object.entries(tspAllocation)
                .slice(0, 6)
                .map(([key, data]) => (
                  <Grid item xs={2} key={key}>
                    <StyledTileDiv
                      active={data.selected}
                      onClick={() => {
                        setTspAllocation((prev) => {
                          const isLFundButton = data.label === 'L Funds' // Check if the "L Fund" button was clicked

                          // If "L Fund" button is being deselected, wipe all L Funds
                          if (isLFundButton && prev[key].selected) {
                            return Object.entries(prev).reduce(
                              (acc, [fundKey, fundData]) => {
                                acc[fundKey] = {
                                  ...fundData,
                                  selected: fundData.label.startsWith('L ')
                                    ? false
                                    : fundData.selected, // Deselect all L Funds
                                  percentage: fundData.label.startsWith('L ')
                                    ? 0
                                    : fundData.percentage, // Reset all L Fund values
                                }
                                return acc
                              },
                              {}
                            )
                          }

                          // Otherwise, just toggle the selection for this fund
                          return {
                            ...prev,
                            [key]: {
                              ...prev[key],
                              selected: !prev[key].selected,
                              percentage: !prev[key].selected
                                ? prev[key].percentage
                                : 0,
                            },
                          }
                        })
                      }}
                    >
                      <Typography variant={'h5'} textTransform="capitalize">
                        {data.label}
                      </Typography>
                    </StyledTileDiv>
                  </Grid>
                ))}
            </Grid>
          </Box>
        </>
      ),
    },
    // WHICH L FUNDS ARE YOU IN?
    {
      title: 'Which Specific L Funds Are You Invested In?',
      desc: (
        <>
          Select all that apply:
          <br />
          <a
            style={{
              color: '#3498DB',
              textDecoration: 'underline',
            }}
            target="_blank"
            href="https://www.TSP.gov"
            rel="noreferrer"
          >
            Can&apos;t remember which funds invested in?
          </a>
        </>
      ),
      action: (
        <>
          <Grid
            container
            sx={{
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
            px={4}
            mt={3}
          >
            <Grid>
              <Grid container item rowSpacing={2} flexDirection={'column'}>
                {Object.entries(tspAllocation)
                  .slice(6, 12)
                  .map(([key, data]) => (
                    <Grid item key={key}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={data.selected}
                            onChange={(e) => {
                              setTspAllocation((prev) => ({
                                ...prev,
                                [key]: {
                                  ...prev[key],
                                  selected: e.target.checked,
                                  percentage: e.target.checked
                                    ? prev[key].percentage
                                    : 0,
                                },
                              }))
                            }}
                            name={key}
                          />
                        }
                        label={data.label}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Grid>
            <Grid item>
              <Grid
                container
                item
                rowSpacing={2}
                flexDirection={'column'}
                pl={8}
              >
                {Object.entries(tspAllocation)
                  .slice(12, 18)
                  .map(([key, data]) => (
                    <Grid item key={key}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={data.selected}
                            onChange={(e) => {
                              setTspAllocation((prev) => ({
                                ...prev,
                                [key]: {
                                  ...prev[key],
                                  selected: e.target.checked,
                                  percentage: e.target.checked
                                    ? prev[key].percentage
                                    : 0,
                                },
                              }))
                            }}
                            name={key}
                          />
                        }
                        label={data.label}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Grid>
          </Grid>
        </>
      ),
    },
    // HOW MUCH ARE YOU INVESTED IN EACH FUND?
    {
      title: 'TSP Fund Current Mix',
      desc: (
        <>
          Fill in how much your current TSP mix is for each fund.
          <br />
          <a
            style={{
              color: '#3498DB',
              textDecoration: 'underline',
            }}
            target="_blank"
            href="https://www.TSP.gov"
            rel="noreferrer"
          >
            Can&apos;t remember your exact mix?
          </a>
        </>
      ),
      action: (
        <>
          {Object.entries(tspAllocation).map(([key, data]) => (
            <>
              {data.label != 'L Funds' && (
                <>
                  {data.selected && (
                    <Grid item mt={2} mr={2} xs={5.5} sm={5.5} key={key}>
                      {renderMuiFormField({
                        label: data.label,
                        autoFocus: false,
                        id: key,
                        placeholder: '0%',
                        inputType: 'percentage',
                        value: data.percentage,
                        onChange: (value) =>
                          setTspAllocation((prev) => ({
                            ...prev,
                            [key]: {
                              ...prev[key],
                              percentage: value,
                            },
                          })),
                      })}
                    </Grid>
                  )}
                </>
              )}
            </>
          ))}

          <Box>
            <Box
              mt={4}
              display={'flex'}
              flexDirection={'row'}
              textAlign={'center'}
              alignItems={'center'}
            >
              <Typography textAlign={'center'} variant="body1">
                TOTAL:
              </Typography>
              <Typography
                textAlign={'center'}
                variant="h3"
                pl={2}
                color={tspTotalMixPercent > 100 ? '#FF0000' : '#262627'}
              >
                {Number.isFinite(tspTotalMixPercent)
                  ? tspTotalMixPercent.toFixed(2)
                  : ''}
                %
              </Typography>
            </Box>
            <Typography variant="body2">
              (Must be 100% in order to continue)
            </Typography>
          </Box>
        </>
      ),
    },
    // REVIEW
    {
      title: "Your total TSP Mix doesn't equal 100%",
      desc: (
        <>
          Try to get your mix percentages as accurate as possible.
          <br />
          If you can’t get it to 100% we will display the{' '}
          <strong>
            {Number.isFinite(tspTotalMixPercent)
              ? tspTotalMixPercent.toFixed(2)
              : ''}
            %
          </strong>{' '}
          you entered for now.
          <br />
          <a
            style={{
              color: '#3498DB',
              textDecoration: 'underline',
            }}
            target="_blank"
            href="https://www.TSP.gov"
            rel="noreferrer"
          >
            Can&apos;t remember which funds invested in?
          </a>
        </>
      ),
      action: <></>,
    },
  ]

  const handleNext = () => {
    if (step === 1) {
      if (tspAllocation.tspLFund?.selected) {
        setStep(2)
        return
      } else if (Object.values(tspAllocation).some((data) => data.selected)) {
        setStep(3)
        return
      } else {
        return
      }
    } else if (
      step === 2 &&
      !Object.entries(tspAllocation).some(
        ([key, data]) =>
          key.includes('L') && key !== 'tspLFund' && data.selected
      )
    ) {
      return
    } else if (step === 3) {
      if (tspTotalMixPercent >= 1 && tspTotalMixPercent < 100) {
        setStep(4)
        return
      } else if (tspTotalMixPercent === 100) {
        handleClose()
        setStep(0)
        return
      } else if (tspTotalMixPercent > 100) {
        return
      }
    } else if (step === 4) {
      handleClose()
      setStep(0)
      return
    } else if (canContinue) {
      setStep((prev) => prev + 1)
      return
    }
  }

  const handleBack = () => {
    if (step == 0) {
      handleClose()
      return
    } else if (step === 3) {
      if (tspAllocation.tspLFund?.selected) {
        setStep(2)
        return
      } else {
        setStep(1)
        return
      }
      // Clear the allocations values when going back to Step 3
    } else if (step == 1) {
      if (tspTotalMixPercent > 0) {
        return
      }
    }

    if (canContinue) {
      setStep((prev) => prev - 1)
    }
  }

  const handleCloseDialog = () => {
    if (step == 3 && tspTotalMixPercent > 100) {
      return
    }
    handleClose()
    setStep(0)
  }

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      fullWidth
      sx={{
        '.MuiPaper-root': {
          padding: '20px 40px', // default for xs screens
          borderRadius: '8px',
          // Use breakpoints to change padding for sm screens
          [theme.breakpoints.up('sm')]: {
            padding: '30px 50px', // padding for sm and up screens
          },

          [theme.breakpoints.up('md')]: {
            padding: '40px 60px', // padding for sm and up screens
          },
        },
      }}
    >
      <DialogTitle
        variant="h4"
        sx={{ fontSize: '24px' }}
        color={'#262627'}
        textAlign="left"
      >
        {steps[step]?.title || ''}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" textAlign="left">
          {steps[step]?.desc || ''}
        </Typography>

        <Grid container mt={0} borderRadius={4}>
          {steps[step]?.action || ''}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="contained"
          sx={[
            {
              borderRadius: 1,

              fontSize: '15px',
              padding: '10px 24px 9px',
              backgroundColor: '#fff',
              color: '#344767',
              fontWeight: '500',
              boxShadow: 'none',
              textTransform: 'none',
            },
            {
              '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                transform: 'none',
                backgroundColor: '#f9f9f9',
                boxShadow: 'none',
              },
            },
          ]}
          onClick={() =>
            canContinue ? handleBack() : (setCanContinue(true), handleBack())
          }
          autoFocus
        >
          BACK
        </Button>
        <Button
          variant="contained"
          sx={{
            borderRadius: 1,
            fontSize: '15px',
            padding: '10px 24px 9px',
            backgroundColor: '#344767',
            color: '#fff',
            fontWeight: 'normal',
            boxShadow: 'none',
            textTransform: 'none',
            '&:hover, &:focus, &:active, &:focus:not(:hover)': {
              transform: 'none',
              backgroundColor: '#293852',
              boxShadow: 'none',
            },
          }}
          onClick={() => {
            canContinue && handleNext()
          }}
          autoFocus
        >
          {step === 4 ? "I'll change it later" : 'NEXT'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
